import { 
  Button, 
  CardContent, 
  FormControlLabel, 
  Grid, 
  Paper, 
  Switch, 
  Typography, 
} from "@mui/material";
import { useState, useEffect } from "react";
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import MySnackbar from "../../../components/MySnackbar";
import TemplateEditorModal from "./TemplateEditorModal";
import { 
  getNotificationConfiguration, 
  updateNotificationConfiguration, 
  updateTemplateNotification,
  NotificationConfig 
} from "../../../api/emailConfigS10Api";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";

type ConfigMap = NotificationConfig[];

export type EMAIL_TYPES = 'ALL_STAFF' | 'BIRTHDAY_USERS' | 'CONTRACTS'

const NOTIFICATION_TYPES = [
    {
        id: 1,
        title: "Onomástico del día: Felicitar solo al Cumpleañero",
        description: "Se envía un correo personalizado solo al cumpleañero del día.",
        emailType: 'BIRTHDAY_USERS' as EMAIL_TYPES,
    },
    {
        id: 2,
        title: "Onomástico del día: Notificar a todo el Personal",
        description: "Se envía un correo personalizado a todo el personal con los cumpleañeros del día.",
        emailType: 'ALL_STAFF' as EMAIL_TYPES,
    },
    {
        id: 3,
        title: "Notificación sobre contratos sin firmar",
        description: "Mensualmente se envía un recordatorio a los colaboradores que tienen contratos sin firmar.",
        emailType: 'CONTRACTS' as EMAIL_TYPES,
    },
];

type VALID_EMAIL_TYPES_IDS = 1 | 2 | 3

const EmailConfigView = () => {
    const { companyselected, connectionId } = useSelector((state: RootState) => state.auth);
    const ruc = companyselected?.RUC 
    const [loading, setLoading] = useState<boolean>(false);
    const [snackBarMessage, setSnackBarMessage] = useState<any | undefined>();

    const [configs, setConfigs] = useState<ConfigMap>([]);

    const [openModal, setOpenModal] = useState<boolean>(false);
    const [modalTarget, setModalTarget] = useState<{ typeId: number; emailType: string } | null>(null);
    const [ConfigurationHasChange, setConfigurationHasChange] = useState<boolean>(false);

    useEffect(() => {
        if (!connectionId || !ruc) {
            return;
        }
        fetchConfigs();
    }, [connectionId, ruc]);

    const fetchConfigs = async () => {
        try {
            const data = await getNotificationConfiguration();
            const configMap: ConfigMap = [];
            data.forEach(item => {
                if (NOTIFICATION_TYPES.find(n => n.id === item.TypeId)) {
                    configMap[item.TypeId] = item;
                }
            });
            setConfigs(configMap);
            setConfigurationHasChange(false);
        } catch (error) {
            console.error("Error al cargar configuraciones", error);
            setSnackBarMessage({ success: false, msg: "Error al cargar configuraciones" });
        }
    };
    const handleOpenEditor = (typeId: number, emailType: string) => {
        if (!ruc) {
            setSnackBarMessage({ success: false, msg: "No se encontró el RUC de la empresa." });
            return;
        }
        setModalTarget({ typeId, emailType });
        setOpenModal(true);
    };

    const handleTemplateSaved = async (htmlContent: string, typeId: number) => {
        try {
            await updateTemplateNotification(typeId, htmlContent);
            const emailType = NOTIFICATION_TYPES.find(n => n.id === typeId)?.emailType
            const url = `https://${process.env.REACT_APP_S3_BUCKET}.s3.amazonaws.com/${ruc}/email-configuration/${emailType}/body.html`;
            setConfigs(prev => prev.map(cfg => 
                cfg?.TypeId === typeId 
                    ? { ...cfg, NotificationBody: htmlContent, TemplateUrl: url } 
                    : cfg
                ));
            setSnackBarMessage({
                success: true,
                msg: "Plantilla guardada exitosamente."
            });
            await fetchConfigs();
        } catch (error) {
            console.error("Error al actualizar plantilla en S10", error);
            setSnackBarMessage({
                success: false,
                msg: "Error al guardar la plantilla en el servidor."
            });
        }
    };

    const handleSaveFinalConfig = async () => {
        setLoading(true);
        try {
            const promises = [];
                console.log('configs', configs)
            for (let i = 0; i < configs.length; i++) {
                const config = configs[i];
                console.log('config', config)
                if (config) {
                    promises.push(updateNotificationConfiguration(i, config.IsActive));
                }
            }
            await Promise.all(promises);
            setSnackBarMessage({
                success: true,
                msg: "Configuración de correos guardada exitosamente."
            });
            setConfigurationHasChange(false)
        } catch (error) {
            console.error("Error al guardar configuración", error);
            setSnackBarMessage({
                success: false,
                msg: "Error al salvar la configuración en el servidor."
            });
        } finally {
            setLoading(false);
        }
    };

    const getConfig = (typeId: VALID_EMAIL_TYPES_IDS): NotificationConfig | null => configs[typeId] || null;

    const renderPanel = (typeId: VALID_EMAIL_TYPES_IDS, title: string, description: string, emailType: EMAIL_TYPES) => {
        const config = getConfig(typeId);
        const isActive = config?.IsActive || false;
        const hasTemplate = !!config?.NotificationBody;

        return (
            <Paper variant="outlined" style={{ padding: "1rem", borderRadius: "8px" }}>
                <Grid container alignItems="center" justifyContent="space-between" marginBottom="1rem">
                    <Grid item xs={10} minHeight={'4em'}>
                        <Typography variant="subtitle1" fontWeight="700">
                            {title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {description}
                        </Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <FormControlLabel
                            control={
                                <Switch
                                checked={isActive}
                                disabled={!hasTemplate}
                                onChange={(e) => {
                                    const finalConfig = [...configs];
                                    console.log(finalConfig)
                                    finalConfig[typeId] = { ...finalConfig[typeId], IsActive: !finalConfig[typeId].IsActive};
                                    setConfigs(finalConfig);
                                    setConfigurationHasChange(true);
                                }}
                                color="primary"
                                />
                            }
                            label={isActive ? "Activo" : "Inactivo"}
                        />
                    </Grid>
                </Grid>
                <CardContent sx={{ borderRadius: "4px", padding: "0 !important" }}>
                    <Grid container alignItems="center" justifyContent="space-between">
                        <Typography variant="body2" color={hasTemplate ? "success.main" : "error.main"} fontWeight="500">
                            {hasTemplate 
                                ? "✓ Plantilla HTML" 
                                : "⚠ Requiere plantilla HTML"
                            }
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleOpenEditor(typeId, emailType)}
                        >
                            {hasTemplate ? "Editar Plantilla" : "Configurar Plantilla"}
                        </Button>
                    </Grid>
                </CardContent>
            </Paper>
        );
    };

    return (
        <Grid item width="100%" marginTop="1.5rem" minHeight="60%">
            <Paper 
                elevation={3}
                style={{
                width: "100%", 
                borderRadius: "0.5rem",
                minHeight: "10rem",
                padding: "2rem 1.5rem"
                }}
            >
                <Grid container spacing={1}>
                    {NOTIFICATION_TYPES.map((type) => (
                        <Grid item xs={12} md={6} key={type.id}>
                        {renderPanel(type.id as VALID_EMAIL_TYPES_IDS, type.title, type.description, type.emailType)}
                        </Grid>
                    ))}
                </Grid>

                <Grid container justifyContent="flex-end" marginTop="3rem" spacing={2}>
                    <Grid item>
                        <Button variant="outlined" startIcon={<CloseIcon />} disabled={loading || !ConfigurationHasChange}>
                            Cancelar
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button 
                            variant="contained"
                            color="primary"
                            disabled={loading || !ConfigurationHasChange}
                            onClick={handleSaveFinalConfig}
                            startIcon={<EmailOutlinedIcon />}
                        >
                            Guardar
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {modalTarget && ruc && (
                <TemplateEditorModal
                    open={openModal}
                    onClose={() => setOpenModal(false)}
                    onSave={handleTemplateSaved}
                    ruc={ruc}
                    emailType={modalTarget.emailType as EMAIL_TYPES}
                    typeId={modalTarget.typeId}
                />
            )}

            {snackBarMessage && (
                <MySnackbar 
                msg={snackBarMessage.msg} 
                severity={snackBarMessage.success ? 'success' : 'error'}
                onClose={() => setSnackBarMessage(undefined)}
                open={true}
                />
            )}
        </Grid>
    );
};

export default EmailConfigView;